import { Link } from '@remix-run/react';
import { AnimatedImage } from '~/routes/_layout.about/components/animated-image';
import { useEffect, useState } from 'react';
import { useStore } from '~/context/store-context';

interface Brand {
  name: string;
  nameSlug: string; // slug used for brand routes and query params
  logo: string;
  logoWhite?: string; // alternative white version for dark backgrounds
  logoDark?: string; // alternative dark version for light backgrounds
  products: string;
  link: string;
  useDarkBg: boolean;
  animation: { left: string; right: string };
  needsWhiteBg: boolean; // true if logo was designed for white backgrounds
  canAdaptToDark?: boolean; // true if brand can work on dark backgrounds with alternative logo
}

interface BrandAvailability {
  isAvailable: boolean;
  availableStores: string[];
}

const brandsData: Brand[] = [
  {
    name: 'PUNCH',
    nameSlug: 'punch',
    logo: '/brandimages/new-punch-logo.webp',
    products: '/brandimages/new-punch-products.webp',
    link: '/brand/punch',
    useDarkBg: false,
    animation: { left: 'animate-slideInLeft', right: 'animate-slideInRight' },
    needsWhiteBg: true,
  },
  {
    name: 'COASTAL SUN',
    nameSlug: 'coastal-sun',
    logo: '/brandimages/new-coastalsun-logo.webp',
    products: '/brandimages/coastal-sun-products.webp',
    link: '/brand/coastal-sun',
    useDarkBg: true,
    animation: { left: 'animate-slideInLeft', right: 'animate-slideInRight' },
    needsWhiteBg: false,
  },
  {
    name: 'BLEM',
    nameSlug: 'blem',
    logo: '/brandimages/new-blem-logo-black.webp',
    products: '/brandimages/new-blem-products.webp',
    link: '/brand/blem',
    useDarkBg: false,
    animation: { left: 'animate-slideInLeft', right: 'animate-slideInRight' },
    needsWhiteBg: true,
  },
  {
    name: 'CONNECTED',
    nameSlug: 'connected',
    logo: '/brandimages/connected-logo.webp',
    products: '/brandimages/connected-products.webp',
    link: '/brand/connected',
    useDarkBg: true,
    animation: { left: 'animate-slideInLeft', right: 'animate-slideInRight' },
    needsWhiteBg: false,
  },
  {
    name: 'TRADITIONAL',
    nameSlug: 'traditional',
    logo: '/brandimages/traditional-logo.webp',
    products: '/brandimages/traditional-product.webp',
    link: '/brand/traditional',
    useDarkBg: false,
    animation: { left: 'animate-slideInLeft', right: 'animate-slideInRight' },
    needsWhiteBg: true,
    canAdaptToDark: true, // Can potentially use white text version on dark bg
  },
];

export default function FeaturedBrandsHome() {
  const { selectedStore } = useStore();
  const [brandAvailability, setBrandAvailability] = useState<Record<string, BrandAvailability>>({});
  const [isLoadingAvailability, setIsLoadingAvailability] = useState(true);

  // Check brand availability when component mounts or store changes
  useEffect(() => {
    if (selectedStore) {
      checkBrandAvailability();
    }
  }, [selectedStore]); // eslint-disable-line react-hooks/exhaustive-deps

  const checkBrandAvailability = async () => {
    setIsLoadingAvailability(true);
    const availability: Record<string, BrandAvailability> = {};

    // Only check current store - we'll filter out unavailable brands
    for (const brand of brandsData) {
      const isAvailableInCurrentStore = await checkBrandInStore(brand.name, selectedStore);

      availability[brand.nameSlug] = {
        isAvailable: isAvailableInCurrentStore,
        availableStores: isAvailableInCurrentStore ? [selectedStore] : [],
      };
    }

    setBrandAvailability(availability);
    setIsLoadingAvailability(false);
  };

  const checkBrandInStore = async (brandName: string, store: string): Promise<boolean> => {
    try {
      // Use our existing brand filtering API to check if brand exists
      const response = await fetch(
        `/api/category-deals?store=${store}&brand=${encodeURIComponent(brandName)}&per_page=1`,
      );

      // Handle API errors gracefully
      if (!response.ok) {
        return false;
      }

      const data = await response.json();
      return data.products && data.products.length > 0;
    } catch (error) {
      return false;
    }
  };

  const renderBrandSection = (brand: Brand, displayIndex: number) => {
    // Use displayIndex to ensure proper alternating backgrounds regardless of filtering
    // Even indexes (0, 2, 4...) = light background, Odd indexes (1, 3, 5...) = dark background
    const useDarkBg = displayIndex % 2 === 1;

    // Determine which logo to use based on background and available alternatives
    const getLogoSrc = () => {
      if (useDarkBg && brand.logoWhite) {
        return brand.logoWhite;
      }
      if (!useDarkBg && brand.logoDark) {
        return brand.logoDark;
      }
      return brand.logo;
    };

    // Determine if we need a fallback white container for logos that need white bg but are on dark bg
    const needsFallbackContainer =
      useDarkBg && brand.needsWhiteBg && !brand.logoWhite && !brand.canAdaptToDark;

    return (
      <section
        key={brand.nameSlug}
        className="relative bg-cover bg-scroll bg-center py-14 md:bg-fixed md:py-24"
        style={{
          backgroundImage: useDarkBg
            ? "url('/images/catalyst-dark-bg-stoner-mobile-optimized.webp')"
            : "url('/images/catalyst-light-bg-stoner-mobile-optimized.webp')",
        }}
      >
        {/* Single Unified Brand Link - Entire Section Clickable */}
        <Link
          to={brand.link}
          aria-label={`View ${brand.name} brand page and shop all products`}
          className="block transition-all duration-200 hover:brightness-105 focus-visible:ring-4 focus-visible:ring-[#ffd100] focus-visible:ring-offset-2 focus-visible:ring-offset-[rgb(32,93,194)] focus-visible:outline-none"
        >
          <div className="mx-auto flex max-w-6xl flex-col items-center px-4 md:flex-row lg:gap-10">
            {/* Left Column: Brand Logo */}
            <div className="w-full p-4 md:w-1/2">
              {needsFallbackContainer ? (
                <div className="mx-auto max-w-lg rounded-lg bg-white p-6 shadow-lg">
                  <AnimatedImage
                    src={getLogoSrc()}
                    alt={`${brand.name} logo`}
                    className="mx-auto object-contain"
                    animationClass={brand.animation.left}
                  />
                </div>
              ) : (
                <AnimatedImage
                  src={getLogoSrc()}
                  alt={`${brand.name} logo`}
                  className={`mx-auto object-contain ${
                    useDarkBg && brand.canAdaptToDark && brand.needsWhiteBg ? 'invert' : ''
                  }`}
                  animationClass={brand.animation.left}
                />
              )}
            </div>
            {/* Right Column: Brand Products */}
            <div className="w-full p-4 md:w-1/2">
              <AnimatedImage
                src={brand.products}
                alt={`${brand.name} cannabis product range`}
                className="mx-auto h-auto w-full object-cover"
                animationClass={brand.animation.right}
                width={600}
                height={400}
              />
            </div>
          </div>

          {/* Visual Action Button - Part of Overall Link */}
          <div className="mt-8 text-center">
            <div className="inline-block min-h-[44px] min-w-[44px] rounded-2xl bg-red-700 px-6 py-4 text-lg font-semibold text-white transition-all duration-200 hover:bg-red-800">
              Shop All {brand.name} Products
            </div>
          </div>
        </Link>
      </section>
    );
  };

  // Filter brands to only show available ones
  const filteredBrands = brandsData.filter((brand) => {
    const availability = brandAvailability[brand.nameSlug];
    return isLoadingAvailability || availability?.isAvailable === true;
  });

  // Smart ordering to optimize background alternation
  const optimizeBackgroundOrder = (brands: Brand[]) => {
    if (brands.length <= 1) return brands;

    const whiteNeeded = brands.filter((b) => b.needsWhiteBg && !b.canAdaptToDark);
    const adaptable = brands.filter((b) => b.needsWhiteBg && b.canAdaptToDark);
    const darkOk = brands.filter((b) => !b.needsWhiteBg);

    const result: Brand[] = [];
    let needsLight = true; // Start with light background

    for (let i = 0; i < brands.length; i++) {
      if (needsLight) {
        // Prefer brands that need white background
        if (whiteNeeded.length > 0) {
          result.push(whiteNeeded.shift()!);
        } else if (adaptable.length > 0) {
          result.push(adaptable.shift()!);
        } else if (darkOk.length > 0) {
          result.push(darkOk.shift()!);
        }
      } else {
        // Prefer brands that work on dark background
        if (darkOk.length > 0) {
          result.push(darkOk.shift()!);
        } else if (adaptable.length > 0) {
          result.push(adaptable.shift()!);
        } else if (whiteNeeded.length > 0) {
          result.push(whiteNeeded.shift()!);
        }
      }
      needsLight = !needsLight;
    }

    return result;
  };

  const availableBrands = isLoadingAvailability
    ? filteredBrands
    : optimizeBackgroundOrder(filteredBrands);

  // Don't render anything if no brands are available (avoid empty space)
  if (!isLoadingAvailability && availableBrands.length === 0) {
    return null;
  }

  return (
    <section className="font-poppins" aria-labelledby="featured-brands-heading">
      <h2 id="featured-brands-heading" className="sr-only">
        Featured Cannabis Brands
      </h2>

      {/* Brand Sections - Only show available brands (limited to 3 for performance) */}
      {availableBrands
        .slice(0, 3)
        .map((brand, displayIndex) => renderBrandSection(brand, displayIndex))}
    </section>
  );
}
